// Sugalarm — the subscription state hook + provider (cycle 464).
//
// One module-level store (the alarmTour / alarmPreview pattern) holding the
// server's `GET /api/me/subscription` answer for the signed-in account. The
// provider, mounted once in the authenticated shell, owns the refetching:
// on mount, on app resume / foreground (`visibilitychange`, which the native
// shells fire on resume too), every 10 minutes while the page is visible,
// and after any purchase / restore attempt. No WebSocket frame this cycle.
//
// The client never flips its own state to subscribed: `confirmWithServer`
// polls until the SERVER's state changes, and the store only ever holds what
// the server said.

import { useEffect, useSyncExternalStore } from "react";

import { getMySubscription, type Subscription } from "../api/subscription";
import {
  attachNativeTransactionListener,
  getBillingSupport,
  subscriptionStepOwed,
} from "../lib/billing";
import type { User } from "../lib/types";

/** The `users.settings` key the server stamps when the step has been shown. */
export const SUBSCRIPTION_STEP_DONE_KEY = "subscription_step_done_at";

export const SUBSCRIPTION_REFRESH_MS = 10 * 60 * 1000;
/** A subscription read that has not answered in this long is abandoned and
 *  reads as a failure (never owed). Holding a first-run screen on a hung
 *  request is how a person gets stranded (safety review A1). */
export const SUBSCRIPTION_READ_TIMEOUT_MS = 5000;
/** The confirmation poll's ceiling and its backing-off delays (ms). */
export const CONFIRM_TIMEOUT_MS = 60_000;
export const CONFIRM_DELAYS_MS = [1000, 2000, 3000, 5000, 8000, 10_000] as const;

interface StoreState {
  data: Subscription | null;
  loaded: boolean;
  error: boolean;
  /** The step finished on THIS device this session (the server stamp may
   *  not have reached the cached user object yet). */
  stepDoneLocally: boolean;
  /** The step is on screen right now (the alarm tour holds on THIS, never
   *  on "owed" alone, so an owed account that lands elsewhere is never held
   *  forever — safety review A2). */
  stepShowing: boolean;
}

const EMPTY: StoreState = {
  data: null,
  loaded: false,
  error: false,
  stepDoneLocally: false,
  stepShowing: false,
};
let state: StoreState = EMPTY;
const listeners = new Set<() => void>();
let inflight: Promise<Subscription | null> | null = null;
/** Every read is tagged with the generation it started in; `clearSubscription`
 *  (sign-out / account change) bumps it, so a reply for the PREVIOUS account
 *  that lands late is dropped, never stored (security review MEDIUM). */
let generation = 0;

function set(next: StoreState): void {
  state = next;
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function snapshot(): StoreState {
  return state;
}

/** A failed read fails OPEN only before the first answer (so nobody is held
 *  on a first-run screen); once the server has answered, a failed REFETCH
 *  (a slow resume network, the 10-minute tick) keeps that answer, so the
 *  step never vanishes mid-flow on a transient error (review round 2, M1). */
function failRead(): void {
  if (state.data != null) {
    if (!state.loaded) set({ ...state, loaded: true });
    return;
  }
  set({ ...state, loaded: true, error: true });
}

/** Fetch the server's answer now (single-flight within one generation,
 *  bounded by `SUBSCRIPTION_READ_TIMEOUT_MS`). Never throws. */
export function refetchSubscription(): Promise<Subscription | null> {
  if (inflight) return inflight;
  const gen = generation;
  // eslint-disable-next-line prefer-const -- read inside its own `finally`
  let p!: Promise<Subscription | null>;
  p = (async () => {
    const ctrl = typeof AbortController === "function" ? new AbortController() : null;
    // ⚠ RACE, never just abort (astra HIGH): a fetch that ignores the signal
    // (or no AbortController at all) would otherwise never settle, pin
    // `inflight`, and leave the gate `pending` — a person held on a blank
    // first-run screen and the alarm tour held for good. The timer settles
    // the read as a failure on its own.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = new Promise<{ ok: false; timedOut: true }>((resolve) => {
      timer = setTimeout(() => {
        ctrl?.abort();
        resolve({ ok: false, timedOut: true });
      }, SUBSCRIPTION_READ_TIMEOUT_MS);
    });
    try {
      const r = await Promise.race([getMySubscription({ signal: ctrl?.signal }), timedOut]);
      if (gen !== generation) return null; // another account's answer: drop it
      if (r.ok) {
        set({ ...state, data: r.data, loaded: true, error: false });
        return r.data;
      }
      failRead();
      return null;
    } catch {
      if (gen === generation) failRead();
      return null;
    } finally {
      clearTimeout(timer);
      if (inflight === p) inflight = null;
    }
  })();
  inflight = p;
  return p;
}

/** Cycle 468 (astra round 2): a read guaranteed to START after this call —
 *  never the answer of a request already in flight, which may have left
 *  before the server committed what the caller just learned of (a WS
 *  `service_state` frame for one of this account's members). Waits for the
 *  in-flight read, then reads again. Never throws. */
export async function refetchSubscriptionFresh(): Promise<Subscription | null> {
  const prior = inflight;
  if (prior) await prior;
  return refetchSubscription();
}

/** Drop the cached answer (sign-out / a different account). Any read still
 *  in flight belongs to the old generation and will be discarded. */
export function clearSubscription(): void {
  generation += 1;
  inflight = null;
  // `stepShowing` belongs to the mounted step (its own effect sets and
  // clears it); the provider's mount-time clear must not unset it.
  set({ ...EMPTY, stepShowing: state.stepShowing });
}

/** The account generation an action starts in; see `applyServerSubscription`. */
export function currentSubscriptionGeneration(): number {
  return generation;
}

/** Cycle 465: the SERVER's answer from a verify that just accepted a store
 *  transaction (the same body `GET /api/me/subscription` returns) becomes the
 *  cached answer at once. Never a client-made value: only what the server
 *  said is ever stored here. `startedIn` is the generation the action began
 *  in (`currentSubscriptionGeneration()`): an answer that lands after a
 *  sign-out or another account's sign-in is dropped, exactly as a late read
 *  is (astra round 2). Returns whether it was applied. */
export function applyServerSubscription(data: Subscription, startedIn: number): boolean {
  if (startedIn !== generation) return false;
  set({ ...state, data, loaded: true, error: false });
  return true;
}

/** The step was finished here (any exit); the server stamp is in flight. */
export function noteSubscriptionStepDone(): void {
  if (!state.stepDoneLocally) set({ ...state, stepDoneLocally: true });
}

/** The step mounted / unmounted (SubscriptionStep's own effect). */
export function noteSubscriptionStepShowing(showing: boolean): void {
  if (state.stepShowing !== showing) set({ ...state, stepShowing: showing });
}

/** For tests only. */
export function _setSubscriptionForTests(
  data: Subscription | null,
  opts: { loaded?: boolean; stepDoneLocally?: boolean; error?: boolean } = {},
): void {
  set({
    data,
    loaded: opts.loaded ?? data != null,
    error: opts.error ?? false,
    stepDoneLocally: opts.stepDoneLocally ?? false,
    stepShowing: false,
  });
}

export function useSubscription(): StoreState & { refetch: () => Promise<Subscription | null> } {
  const s = useSyncExternalStore(subscribe, snapshot, snapshot);
  return { ...s, refetch: refetchSubscription };
}

export function stepDoneOnServer(user: User | null | undefined): boolean {
  const v = user?.settings?.[SUBSCRIPTION_STEP_DONE_KEY];
  return typeof v === "string" && v.length > 0;
}

export interface StepGate {
  /** Show the step now. */
  owed: boolean;
  /** The answer is not known yet (the subscription read is in flight on a
   *  surface that could owe the step). Callers HOLD the next first-run
   *  screen; a failed read resolves to not-owed, never a stuck person. */
  pending: boolean;
}

/** The pure gate both `LoginScreen` and the alarm-tour trigger read. */
export function subscriptionStepGate(input: {
  signedIn: boolean;
  user: User | null | undefined;
  store: Pick<StoreState, "data" | "loaded" | "error" | "stepDoneLocally">;
  support: ReturnType<typeof getBillingSupport>;
  /** The frame renders the kiosk layout. A kiosk-shaped screen never owes
   *  the step, even when no kiosk flag or device token marks it (a Pi-style
   *  Chromium matched only by the media query — safety review A3). */
  kioskVariant?: boolean;
}): StepGate {
  const { signedIn, user, store, kioskVariant = false } = input;
  const support = kioskVariant ? { supported: false, platform: "kiosk" as const } : input.support;
  if (!signedIn) return { owed: false, pending: false };
  const done = store.stepDoneLocally || stepDoneOnServer(user);
  // Could this surface owe it at all, whatever the state? If not, nothing
  // waits on the read (today's native apps: no Billing plugin yet).
  const couldOwe = subscriptionStepOwed({
    state: "none",
    platform: support.platform,
    supported: support.supported,
    done,
  });
  if (!couldOwe) return { owed: false, pending: false };
  if (!store.loaded) return { owed: false, pending: true };
  if (store.error || store.data == null) return { owed: false, pending: false };
  return {
    owed: subscriptionStepOwed({
      state: store.data.state,
      platform: support.platform,
      supported: support.supported,
      done,
    }),
    pending: false,
  };
}

export function useSubscriptionStepGate(
  signedIn: boolean,
  user: User | null | undefined,
  kioskVariant = false,
): StepGate & { subscription: Subscription | null; stepShowing: boolean } {
  const store = useSyncExternalStore(subscribe, snapshot, snapshot);
  const gate = subscriptionStepGate({
    signedIn,
    user,
    store,
    support: getBillingSupport(),
    kioskVariant,
  });
  return { ...gate, subscription: store.data, stepShowing: store.stepShowing };
}

/** A fingerprint of what the person would see change — the state and the
 *  dates/source behind it. */
function fingerprint(s: Subscription | null): string {
  if (s == null) return "null";
  return [s.state, s.source, s.trial_ends_at, s.renews_at, s.grace_ends_at].join("|");
}

/** After a native purchase / restore resolves, poll the server (up to 60 s,
 *  backing off) until its answer differs from `before`. Resolves true when
 *  the server confirmed a change, false at the ceiling. The caller shows
 *  "Confirming with the store..." for the whole wait. */
export async function confirmWithServer(
  before: Subscription | null,
  opts: {
    fetch?: () => Promise<Subscription | null>;
    sleep?: (ms: number) => Promise<void>;
    now?: () => number;
  } = {},
): Promise<boolean> {
  const fetchOnce = opts.fetch ?? refetchSubscription;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const now = opts.now ?? (() => Date.now());
  const start = now();
  const was = fingerprint(before);
  for (let i = 0; ; i += 1) {
    const delay = CONFIRM_DELAYS_MS[Math.min(i, CONFIRM_DELAYS_MS.length - 1)]!;
    const left = CONFIRM_TIMEOUT_MS - (now() - start);
    if (left <= 0) return false;
    await sleep(Math.min(delay, left));
    const got = await fetchOnce();
    if (got != null && fingerprint(got) !== was) return true;
  }
}

/** Mounted once in the authenticated shell: owns every refetch trigger.
 *  Keyed on the ACCOUNT, not only on being signed in: a different account
 *  signing in inside the same shell clears the old answer and reads afresh. */
export function SubscriptionProvider({
  signedIn,
  accountId,
}: {
  signedIn: boolean;
  accountId: string | null;
}): null {
  useEffect(() => {
    if (!signedIn) {
      clearSubscription();
      return;
    }
    clearSubscription();
    void refetchSubscription();
    // Cycle 465: StoreKit's transaction updates (a renewal, a refund, an
    // Ask to Buy approval, a purchase finished elsewhere) reach the server
    // through the same verify as a purchase, then the answer is re-read.
    // Attached only while signed in: the verify needs this account's JWT.
    const startedIn = generation;
    const detachNative = attachNativeTransactionListener((accepted) => {
      if (accepted) applyServerSubscription(accepted, startedIn);
      void refetchSubscription();
    });
    const onVis = () => {
      if (document.visibilityState === "visible") void refetchSubscription();
    };
    document.addEventListener("visibilitychange", onVis);
    const t = setInterval(() => {
      if (document.visibilityState === "visible") void refetchSubscription();
    }, SUBSCRIPTION_REFRESH_MS);
    return () => {
      detachNative();
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(t);
      // The shell unmounts on sign-out: never carry one account's answer
      // into the next session.
      clearSubscription();
    };
  }, [signedIn, accountId]);
  return null;
}
